import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoAdd,
  IoCameraOutline,
  IoImagesOutline,
  IoReceiptOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoTimeOutline,
  IoTrashOutline,
  IoChevronForward,
  IoRefreshOutline,
  IoDocumentTextOutline,
} from "react-icons/io5";
import api from "@/api";
import { STORE_LABELS, useI18n } from "@/i18n";
import { AppHeader } from "@/components/AppHeader";
import { BottomNav } from "@/components/BottomNav";
import { tapLight, tapSuccess } from "@/utils/haptics";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};

/**
 * Resize an image on the client to `maxDim` pixels on its longest side and
 * re-encode as JPEG at the requested quality. Falls back gracefully if the
 * browser can't create a canvas (returns the original file).
 * Typical result: 4MB camera JPEG → ~500-800KB, ~3x faster upload + OCR.
 */
const compressImage = (file, { maxDim = 2000, quality = 0.82 } = {}) =>
  new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith("image/")) {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const { width: w, height: h } = img;
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const cw = Math.round(w * scale);
        const ch = Math.round(h * scale);
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        if (!ctx) { URL.revokeObjectURL(url); resolve(file); return; }
        ctx.drawImage(img, 0, 0, cw, ch);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) { resolve(file); return; }
            // Preserve original filename base, force jpg extension.
            const base = (file.name || "invoice").replace(/\.[^.]+$/, "");
            const compressed = new File([blob], `${base}.jpg`, { type: "image/jpeg" });
            // If compression made it BIGGER (rare on tiny inputs), keep original.
            resolve(compressed.size < file.size ? compressed : file);
          },
          "image/jpeg",
          quality
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });

/**
 * Landing page for the "Controlla la tua fattura" section (per-store).
 * Lists past invoice checks and offers a big "+ Nuova fattura" call-to-action that
 * triggers photo upload and OCR, then navigates to the detail checklist.
 */
export default function InvoiceCheckListPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // ---- Multi-page invoice capture ----
  // Users typically snap 3-5 photos sequentially with the mobile camera. We keep
  // each captured page as { id, file, previewUrl } in a local array and only
  // POST to /invoices/parse when the user confirms with "Elabora fattura".
  const [pages, setPages] = useState([]);       // [{id, file, previewUrl}]
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // Progress state while OCR is running. Since Gemini's response is a single
  // blocking call (~15-30s), we synthesise "phases" over time to reassure the
  // direttore that work is happening.
  const [progressStage, setProgressStage] = useState("");
  const [progressElapsed, setProgressElapsed] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices`, { params: { store_id: storeId } });
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  // Timer while uploading: updates elapsed seconds and swaps phase text every
  // ~few seconds so the user knows the app is still working.
  useEffect(() => {
    if (!uploading) return undefined;
    const start = Date.now();
    setProgressElapsed(0);
    const tick = setInterval(() => {
      const el = Math.floor((Date.now() - start) / 1000);
      setProgressElapsed(el);
      // Phase transitions (only if the caller hasn't already set a stage manually).
      setProgressStage((prev) => {
        if (el < 3) return prev || "Preparo l'immagine…";
        if (el < 8) return "Invio al server…";
        if (el < 18) return "Sto leggendo le righe della fattura…";
        if (el < 30) return "Ancora un attimo, quasi fatto…";
        return "Ci vuole un po' più del solito, tengo duro…";
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [uploading]);

  const onFile = async (files) => {
    // Add captured photo(s) to the pending pages array. The user can keep adding
    // pages before pressing "Elabora fattura" to actually submit them.
    const list = Array.isArray(files) ? files : (files ? [files] : []);
    if (list.length === 0) return;
    const totalAfter = pages.length + list.length;
    if (totalAfter > 8) {
      toast.error("Massimo 8 pagine per fattura");
      return;
    }
    const additions = list.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
    }));
    setPages((prev) => [...prev, ...additions]);
    tapLight();
  };

  const removePage = (id) => {
    setPages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
    tapLight();
  };

  const clearPages = () => {
    pages.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setPages([]);
  };

  // Clean up preview URLs when component unmounts
  useEffect(() => () => {
    pages.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitPages = async () => {
    if (pages.length === 0) return;
    setUploading(true);
    setProgressStage(pages.length > 1 ? `Preparo ${pages.length} pagine…` : "Preparo l'immagine…");
    setProgressElapsed(0);
    try {
      // Compress each page in parallel client-side.
      const compressed = await Promise.all(pages.map(async (p) => {
        try { return await compressImage(p.file, { maxDim: 2000, quality: 0.82 }); }
        catch { return p.file; }
      }));
      setProgressStage(pages.length > 1 ? `Invio ${pages.length} pagine al server…` : "Invio al server…");
      const fd = new FormData();
      for (const f of compressed) {
        fd.append("image", f, f.name || "invoice.jpg");
      }
      const { data } = await api.post(`/invoices/parse?store_id=${encodeURIComponent(storeId)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // multi-page can take a while
      });
      tapSuccess();
      const pagesLabel = pages.length > 1 ? ` (${pages.length} pagine)` : "";
      toast.success(`Fattura caricata${pagesLabel} · ${data.items?.length || 0} righe`);
      clearPages();
      navigate(`/catalog/${storeId}/invoice-check/${data.id}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || "Credito Emergent LLM esaurito. Ricarica il saldo.");
      } else if (status === 429) {
        toast.error("Servizio OCR occupato. Riprova tra qualche secondo.");
      } else if (status === 422) {
        toast.error(detail || "Foto non leggibile. Prova con più luce e meno inclinazione.");
      } else if (status === 413) {
        toast.error(detail || "Immagine troppo grande.");
      } else if (e?.code === "ECONNABORTED") {
        toast.error("Timeout OCR. Prova con meno pagine o foto più piccole.");
      } else {
        toast.error(detail || "Impossibile leggere la fattura");
      }
    } finally {
      setUploading(false);
      setProgressStage("");
      setProgressElapsed(0);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Elimina questo controllo fattura?")) return;
    setDeletingId(id);
    try {
      await api.delete(`/invoices/${id}`);
      tapLight();
      setInvoices((arr) => arr.filter((i) => i.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mobile-frame with-bottom-bar" data-testid="invoice-check-list-page">
      <AppHeader
        title={`Controlla fattura · ${t(STORE_LABELS[storeId]) || ""}`}
        showBack
        backTo={`/catalog/${storeId}`}
      />

      <div className="tf-container">
        <div className="tf-field" style={{ marginTop: 12 }}>
          {/* Hidden inputs — triggered programmatically by the visible buttons.
              We use one input per action so the `capture` attribute doesn't leak
              into the gallery flow, and each `onChange` resets its own value so
              the same photo can be taken again immediately. */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            data-testid="invoice-camera-input"
            style={{ display: "none" }}
            onChange={(e) => {
              const list = Array.from(e.target.files || []);
              e.target.value = "";
              if (list.length) onFile(list);
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            data-testid="invoice-gallery-input"
            style={{ display: "none" }}
            onChange={(e) => {
              const list = Array.from(e.target.files || []);
              e.target.value = "";
              if (list.length) onFile(list);
            }}
          />

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="tf-btn-primary"
              data-testid="invoice-camera-btn"
              disabled={uploading || pages.length >= 8}
              onClick={() => cameraInputRef.current?.click()}
              style={{
                flex: 1,
                background: uploading || pages.length >= 8 ? "#4a4a4a" : "#0a0a0a",
                cursor: uploading || pages.length >= 8 ? "not-allowed" : "pointer",
                opacity: uploading || pages.length >= 8 ? 0.6 : 1,
              }}
            >
              <IoCameraOutline size={20} />
              {pages.length === 0 ? "Scatta" : "Aggiungi pagina"}
            </button>
            <button
              type="button"
              className="tf-btn-primary"
              data-testid="invoice-gallery-btn"
              disabled={uploading || pages.length >= 8}
              onClick={() => galleryInputRef.current?.click()}
              style={{
                flex: 1,
                background: "transparent",
                color: "#0a0a0a",
                border: "1.5px solid #0a0a0a",
                cursor: uploading || pages.length >= 8 ? "not-allowed" : "pointer",
                opacity: uploading || pages.length >= 8 ? 0.5 : 1,
              }}
            >
              <IoImagesOutline size={20} />
              Galleria
            </button>
          </div>
          <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 8, textAlign: "center" }}>
            {pages.length === 0
              ? "Scatta o carica la foto della bolla / DDT — riconosce automaticamente le righe."
              : `${pages.length} / 8 pagine pronte. Scatta le pagine successive o Elabora la fattura.`}
          </div>
        </div>

        {/* Preview strip: shows all pending pages as thumbnails with delete + confirm CTA */}
        {pages.length > 0 && (
          <div
            data-testid="invoice-pages-strip"
            style={{
              marginTop: 4,
              background: "#0a0a0a",
              color: "#fff",
              borderRadius: 16,
              padding: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14 }}>
                <IoDocumentTextOutline size={18} />
                Pagine ({pages.length})
              </div>
              <button
                type="button"
                data-testid="invoice-pages-clear"
                onClick={clearPages}
                disabled={uploading}
                style={{
                  background: "transparent",
                  color: "#fca5a5",
                  border: "none",
                  fontSize: 12,
                  cursor: uploading ? "not-allowed" : "pointer",
                  padding: 4,
                }}
              >
                Azzera tutto
              </button>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                paddingBottom: 4,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {pages.map((p, idx) => (
                <div
                  key={p.id}
                  data-testid={`invoice-page-thumb-${idx}`}
                  style={{
                    position: "relative",
                    width: 84,
                    height: 108,
                    borderRadius: 10,
                    flexShrink: 0,
                    overflow: "hidden",
                    background: "#1a1a1a",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <img
                    src={p.previewUrl}
                    alt={`Pagina ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: 4,
                      top: 4,
                      background: "#E2001A",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "2px 6px",
                      borderRadius: 6,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <button
                    type="button"
                    data-testid={`invoice-page-remove-${idx}`}
                    aria-label={`Rimuovi pagina ${idx + 1}`}
                    disabled={uploading}
                    onClick={() => removePage(p.id)}
                    style={{
                      position: "absolute",
                      right: 4,
                      top: 4,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: uploading ? "not-allowed" : "pointer",
                      padding: 0,
                    }}
                  >
                    <IoCloseCircle size={22} />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              data-testid="invoice-submit-btn"
              disabled={uploading || pages.length === 0}
              onClick={submitPages}
              style={{
                marginTop: 12,
                width: "100%",
                background: "#E2001A",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "14px 16px",
                fontWeight: 800,
                fontSize: 15,
                cursor: uploading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                opacity: uploading ? 0.7 : 1,
              }}
            >
              {uploading ? (
                <>
                  <IoRefreshOutline size={18} className="tf-spin" />
                  Sto leggendo…
                </>
              ) : (
                <>
                  <IoCheckmarkCircle size={20} />
                  Elabora fattura ({pages.length} {pages.length === 1 ? "pagina" : "pagine"})
                </>
              )}
            </button>
          </div>
        )}

        <div className="tf-h2" style={{ marginTop: 20, fontSize: 16 }}>Fatture passate</div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <IoRefreshOutline size={22} className="tf-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="tf-small" style={{ color: "var(--tf-muted)", padding: 24, textAlign: "center" }}>
            Nessuna fattura caricata finora.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }} data-testid="invoice-list">
            {invoices.map((inv) => {
              const total = inv.items_total || 0;
              const arrived = inv.items_arrived || 0;
              const missing = inv.items_missing || 0;
              const pending = inv.items_pending || 0;
              const done = total > 0 && pending === 0;
              return (
                <div
                  key={inv.id}
                  data-testid={`invoice-row-${inv.id}`}
                  onClick={() => navigate(`/catalog/${storeId}/invoice-check/${inv.id}`)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: done ? "#16a34a" : "#0a0a0a", color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IoReceiptOutline size={22} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {inv.supplier || "Fattura"} {inv.invoice_number ? `· #${inv.invoice_number}` : ""}
                    </div>
                    <div className="tf-small" style={{ color: "var(--tf-muted)" }}>
                      {inv.invoice_date || fmtDate(inv.created_at)}
                      {" · "}
                      {total} righe · <span style={{ color: "#16a34a" }}>{arrived} ✓</span>
                      {missing > 0 ? <> · <span style={{ color: "#dc2626" }}>{missing} ✗</span></> : null}
                      {pending > 0 ? <> · <span style={{ color: "#a16207" }}>{pending} da controllare</span></> : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`invoice-delete-${inv.id}`}
                    aria-label="Elimina"
                    disabled={deletingId === inv.id}
                    onClick={(e) => { e.stopPropagation(); remove(inv.id); }}
                    style={{
                      border: "none", background: "transparent", color: "#dc2626",
                      padding: 6, cursor: "pointer",
                    }}
                  >
                    <IoTrashOutline size={18} />
                  </button>
                  <IoChevronForward size={18} style={{ color: "var(--tf-muted)" }} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />

      {uploading && (
        <div
          className="tf-sheet-backdrop"
          data-testid="invoice-progress-overlay"
          style={{ zIndex: 60 }}
        >
          <div
            className="tf-sheet"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 340,
              textAlign: "center",
              padding: "28px 24px",
              background: "#0a0a0a",
              color: "#fff",
              borderRadius: 20,
            }}
          >
            <div
              style={{
                width: 72, height: 72, borderRadius: "50%",
                margin: "0 auto 16px",
                background: "#1a1a1a",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 0 0 6px rgba(226,0,26,0.15)",
              }}
            >
              <IoRefreshOutline size={30} className="tf-spin" color="#E2001A" />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }} data-testid="progress-stage">
              {progressStage || "Preparo l'immagine…"}
            </div>
            <div
              className="tf-small"
              style={{ color: "rgba(255,255,255,0.6)", marginBottom: 16 }}
              data-testid="progress-elapsed"
            >
              {progressElapsed}s
            </div>
            {/* Indeterminate progress bar */}
            <div
              style={{
                position: "relative",
                height: 4,
                borderRadius: 2,
                background: "rgba(255,255,255,0.12)",
                overflow: "hidden",
              }}
            >
              <div
                className="tf-progress-bar-indeterminate"
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: "40%",
                  background: "#E2001A",
                  borderRadius: 2,
                }}
              />
            </div>
            <div
              className="tf-small"
              style={{ color: "rgba(255,255,255,0.5)", marginTop: 14, lineHeight: 1.4 }}
            >
              L&apos;OCR normalmente impiega 15-25 secondi.<br />Tieni l&apos;app aperta.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
